import { useState, useEffect } from "react";
import { toast } from "sonner";
import { RefreshCw, Plus, Clock } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, LoadingScreen, EmptyState } from "@/components/common";

export default function Replacements() {
  const [rows, setRows] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ contract_id: "", location: "", guard_required: 1, shift: "", reason: "", required_by: "" });

  const load = () => api.get("/replacements/mine").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/contracts/mine").then((r) => setContracts(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.location || !f.shift || !f.reason) return toast.error("Fill location, shift and reason");
    try {
      const { data } = await api.post("/replacements", { ...f, guard_required: Number(f.guard_required) });
      toast.success(`Request sent. Replacement guard expected within ${data.eta_minutes} minutes.`);
      setOpen(false); setF({ contract_id: "", location: "", guard_required: 1, shift: "", reason: "", required_by: "" }); load();
    } catch (e) { toast.error("Failed to request"); }
  };

  return (
    <DashboardShell menu={customerMenu} title="Emergency Replacement">
      <PageHeader title="Replacement Requests" subtitle="Request an emergency guard replacement — eligible agencies respond fast."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-600" data-testid="request-replacement-btn"><Plus size={16} className="mr-1" /> Request Replacement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Emergency Guard Replacement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Contract (optional)</Label>
                  <Select value={f.contract_id} onValueChange={(v) => { const c = contracts.find((x) => x.id === v); setF({ ...f, contract_id: v, location: c?.service_location || f.location }); }}>
                    <SelectTrigger data-testid="rep-contract"><SelectValue placeholder="Select contract" /></SelectTrigger>
                    <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.agency_name} · {c.service_location}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Location *</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="rep-location" /></div>
                  <div><Label className="text-xs">Guards Required</Label><Input type="number" min={1} value={f.guard_required} onChange={(e) => setF({ ...f, guard_required: e.target.value })} data-testid="rep-guards" /></div>
                  <div><Label className="text-xs">Shift *</Label>
                    <Select value={f.shift} onValueChange={(v) => setF({ ...f, shift: v })}>
                      <SelectTrigger data-testid="rep-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
                      <SelectContent>{["Day (8AM-8PM)", "Night (8PM-8AM)", "Full Day"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Required By</Label><Input type="datetime-local" value={f.required_by} onChange={(e) => setF({ ...f, required_by: e.target.value })} data-testid="rep-required-by" /></div>
                </div>
                <div><Label className="text-xs">Reason *</Label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="rep-reason" /></div>
              </div>
              <DialogFooter><Button className="bg-brand hover:bg-brand-600" onClick={submit} data-testid="submit-replacement-btn">Send Request</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={RefreshCw} title="No replacement requests" hint="Request an emergency replacement whenever a guard is unavailable." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-navy-200 rounded-xl p-5" data-testid={`replacement-${r.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-heading font-semibold text-navy-900">{r.guard_required} guard(s) · {r.location}</h3><p className="text-xs text-navy-500 mt-0.5">{r.shift} · {r.reason}</p></div>
                <StatusPill status={r.status} />
              </div>
              <div className="mt-3 flex items-center gap-1 text-sm text-brand font-medium"><Clock size={15} /> Replacement guard expected within {r.eta_minutes} minutes.</div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
